import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiMail, FiX, FiSend, FiCheckCircle, FiAlertCircle, FiLoader } from 'react-icons/fi';
import './SendEmailModal.css';

function SendEmailModal({ selectedCount, selectedIds, onClose, onSuccess }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/email-templates');
      const active = response.data.data.filter(t => t.is_active);
      setTemplates(active);
    } catch (err) {
      setError('Failed to load templates.');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      setError('Please select an email template.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await axios.post('/api/email-campaigns/send', {
        company_ids: selectedIds,
        template_id: selectedTemplate.id,
        campaign_name: campaignName.trim() || undefined
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send emails. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="send-modal" onClick={e => e.stopPropagation()}>
        <div className="send-modal-header">
          <div className="send-modal-title">
            <FiMail size={20} />
            <span>Send Email Campaign</span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {!result ? (
          <div className="send-modal-body">
            <div className="recipient-badge">
              <FiMail size={14} />
              <span>Sending to <strong>{selectedCount}</strong> selected {selectedCount === 1 ? 'company' : 'companies'}</span>
            </div>

            {selectedCount > 500 && (
              <div className="send-warning">
                <FiAlertCircle size={16} />
                <span>Maximum 500 recipients per campaign. Please narrow your selection.</span>
              </div>
            )}

            <div className="send-form-group">
              <label>Campaign Name <span className="optional">(optional)</span></label>
              <input
                type="text"
                placeholder="e.g., April Outreach 2026"
                value={campaignName}
                onChange={e => setCampaignName(e.target.value)}
                disabled={loading}
                className="send-input"
              />
            </div>

            <div className="send-form-group">
              <label>Email Template <span className="required">*</span></label>
              {loadingTemplates ? (
                <div className="loading-templates">Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className="no-templates-msg">
                  No active templates found. Create one in the Email Templates tab.
                </div>
              ) : (
                <div className="template-select-list">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className={`template-option ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="template-option-radio">
                        <div className={`radio-dot ${selectedTemplate?.id === template.id ? 'active' : ''}`} />
                      </div>
                      <div className="template-option-info">
                        <div className="template-option-name">{template.name}</div>
                        <div className="template-option-subject">{template.subject}</div>
                        <div className="template-option-type">{getTypeLabel(template.template_type)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="send-error">
                <FiAlertCircle size={15} />
                <span>{error}</span>
              </div>
            )}

            <div className="send-modal-footer">
              <button className="btn-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button
                className="btn-send"
                onClick={handleSend}
                disabled={loading || !selectedTemplate || selectedCount > 500 || loadingTemplates}
              >
                {loading ? (
                  <>
                    <FiLoader size={16} className="spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend size={16} />
                    Send to {selectedCount} {selectedCount === 1 ? 'Company' : 'Companies'}
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="send-modal-body">
            <div className="send-result">
              <div className={`result-icon ${result.failed === 0 ? 'success' : 'partial'}`}>
                <FiCheckCircle size={40} />
              </div>
              <h3 className="result-title">
                {result.failed === 0 ? 'Campaign Sent Successfully!' : 'Campaign Completed with Errors'}
              </h3>
              <p className="result-campaign-name">{result.campaign_name}</p>

              <div className="result-stats">
                <div className="result-stat sent">
                  <span className="stat-number">{result.sent}</span>
                  <span className="stat-label">Sent</span>
                </div>
                {result.failed > 0 && (
                  <div className="result-stat failed">
                    <span className="stat-number">{result.failed}</span>
                    <span className="stat-label">Failed</span>
                  </div>
                )}
                <div className="result-stat total">
                  <span className="stat-number">{result.total}</span>
                  <span className="stat-label">Total</span>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="result-errors">
                  <p className="result-errors-title">Failed deliveries (first {result.errors.length}):</p>
                  <ul>
                    {result.errors.map((e, i) => (
                      <li key={i}><span className="err-email">{e.email}</span> — {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="send-modal-footer">
              <button className="btn-send" onClick={handleDone}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getTypeLabel(type) {
  const map = { initial: 'Initial Outreach', followup_1: 'Follow-up 1', followup_2: 'Follow-up 2' };
  return map[type] || type;
}

export default SendEmailModal;
